/**
 * Agent-Readiness fix-snippet builders — pure strings.
 *
 * Each builder takes the DETERMINISTIC inputs the readiness page already
 * has (merchant domain, blocked-bot list, platform kind, cdn hint,
 * install source, site_key_hash, …) and returns the EXACT, pastable
 * text the drawer offers in a Copy block. No clocks, no randomness,
 * no network — these are rendered through `navigator.clipboard` only
 * when the merchant presses the Copy button.
 *
 * We keep them here (and not inside FixDrawer.tsx) so:
 *   - the snippets are unit-tested in @sill/types without pulling in
 *     React / jsdom (same pattern as `buildReadinessChecks` and the
 *     ranker);
 *   - the dashboard, marketing copy deck, and any future API-exposed
 *     "fix recipe" surface share ONE source of truth for what to paste.
 *
 * Rules:
 *   - Never emit `example.com` — always the merchant's real domain.
 *   - Blocked-bot lines come from `ai_bots_status`, not a hardcoded
 *     list. If every known bot is blocked by the general rule we emit
 *     the full MAJOR_BOT_IDS set.
 *   - JSON-LD is JSON.parse-safe — tests assert it round-trips.
 *   - Content-Signal syntax: `search=yes, ai-input=yes, ai-train=no`
 *     (per the ARD / cloudflare-content-signal convention).
 */
/** The seven known assistant/search bots the readiness page scans. */
export const KNOWN_AI_BOTS = [
    'GPTBot',
    'ChatGPT-User',
    'ClaudeBot',
    'PerplexityBot',
    'Google-Extended',
    'Googlebot',
    'Bingbot',
];
/** Normalise a domain the merchant typed (strip scheme, trailing slash). */
export function normaliseDomain(input) {
    const raw = (input ?? '').trim();
    if (raw === '')
        return 'your-site.com';
    return raw
        .replace(/^https?:\/\//i, '')
        .replace(/\/+$/, '')
        .toLowerCase();
}
/* ================================================================== */
/* 1. Crawler access blocked — Cloudflare WAF rule                     */
/* ================================================================== */
/**
 * `cloudflareWafSkipRule` — the expression a Cloudflare WAF custom rule
 * needs to Skip verified AI + search crawlers. Pastes directly into the
 * expression field under Security → WAF → Custom rules.
 */
export function cloudflareWafSkipRule() {
    return '(cf.verified_bot_category in {"AI Crawler" "AI Assistant" "Search Engine Crawler"})';
}
/** Short English copy-for-developer summary of the Cloudflare fix. */
export function cloudflareCrawlerFixSteps(blockedBots) {
    const bots = blockedBots.length > 0
        ? blockedBots.join(', ')
        : KNOWN_AI_BOTS.join(', ');
    return [
        `Please allow the following crawlers in Cloudflare → Security → Bots → AI Crawl Control: ${bots}.`,
        'If AI Scrape protection is enabled, set the assistants above to Allow (not Block).',
        'In Security → WAF → Custom rules, add a rule with action Skip and the expression',
        `  ${cloudflareWafSkipRule()}`,
        'Save and re-run the readiness check.',
    ].join('\n');
}
/* ================================================================== */
/* 2. robots.txt — per-bot allow lines                                 */
/* ================================================================== */
/**
 * `robotsAllowBlock` — the exact lines to paste under existing content
 * in robots.txt. One `User-agent:` + `Allow: /` per blocked bot. We
 * emit a trailing newline so the paste survives in editors that need
 * a POSIX line ending.
 */
export function robotsAllowBlock(blockedBots) {
    const names = blockedBots.length > 0 ? blockedBots : KNOWN_AI_BOTS;
    const groups = names.map((bot) => `User-agent: ${bot}\nAllow: /`);
    return groups.join('\n\n') + '\n';
}
/* ================================================================== */
/* 3. robots.txt missing — a complete file                             */
/* ================================================================== */
export function robotsStarterFile(domain) {
    const d = normaliseDomain(domain);
    return [
        'User-agent: *',
        'Allow: /',
        `Content-Signal: ${contentSignalLineValue()}`,
        '',
        `Sitemap: https://${d}/sitemap.xml`,
        '',
    ].join('\n');
}
/* ================================================================== */
/* 4. Content signals                                                  */
/* ================================================================== */
/** Canonical default value: search allowed, AI input allowed, AI train denied. */
export function contentSignalLineValue() {
    return 'search=yes, ai-input=yes, ai-train=no';
}
/** The one line to paste directly under `User-agent: *`. */
export function contentSignalLine() {
    return `Content-Signal: ${contentSignalLineValue()}`;
}
/* ================================================================== */
/* 5. Sitemap robots line                                              */
/* ================================================================== */
export function sitemapRobotsLine(domain) {
    return `Sitemap: https://${normaliseDomain(domain)}/sitemap.xml`;
}
/* ================================================================== */
/* 6. Structured data — JSON-LD for an organisation + a product        */
/* ================================================================== */
/**
 * `organizationJsonLd` — a minimal, JSON.parse-safe Organization block
 * for the merchant's homepage `<head>`. Just name + url; the merchant
 * fills in the rest.
 */
export function organizationJsonLd(domain, name) {
    const d = normaliseDomain(domain);
    const n = (name ?? '').trim().length > 0 ? name.trim() : d;
    const payload = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: n,
        url: `https://${d}`,
    };
    return `<script type="application/ld+json">\n${JSON.stringify(payload, null, 2)}\n</script>`;
}
/**
 * `productJsonLd` — a Product block with a nested Offer, shipping and
 * return-policy stubs. Placeholders are explicit (`<...>`) so pasting
 * it without edits raises a validator warning the merchant can't miss.
 */
export function productJsonLd(domain) {
    const d = normaliseDomain(domain);
    const payload = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: '<Product name>',
        sku: '<SKU>',
        image: [`https://${d}/images/<product-image>.jpg`],
        description: '<Short product description>',
        offers: {
            '@type': 'Offer',
            url: `https://${d}/products/<product-slug>`,
            priceCurrency: 'USD',
            price: '<price>',
            availability: 'https://schema.org/InStock',
            hasMerchantReturnPolicy: {
                '@type': 'MerchantReturnPolicy',
                applicableCountry: 'US',
                returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
                merchantReturnDays: 30,
                returnMethod: 'https://schema.org/ReturnByMail',
                returnFees: 'https://schema.org/FreeReturn',
            },
            shippingDetails: {
                '@type': 'OfferShippingDetails',
                shippingRate: {
                    '@type': 'MonetaryAmount',
                    value: '<amount>',
                    currency: 'USD',
                },
                shippingDestination: {
                    '@type': 'DefinedRegion',
                    addressCountry: 'US',
                },
                deliveryTime: {
                    '@type': 'ShippingDeliveryTime',
                    handlingTime: {
                        '@type': 'QuantitativeValue',
                        minValue: 0,
                        maxValue: 1,
                        unitCode: 'DAY',
                    },
                    transitTime: {
                        '@type': 'QuantitativeValue',
                        minValue: 2,
                        maxValue: 5,
                        unitCode: 'DAY',
                    },
                },
            },
        },
    };
    return `<script type="application/ld+json">\n${JSON.stringify(payload, null, 2)}\n</script>`;
}
/* ================================================================== */
/* 6b. Catalog-based Product JSON-LD                                   */
/* ================================================================== */
/** Closed enum of schema.org availability values. */
const CATALOG_AVAIL_TO_SCHEMA = {
    in_stock: 'https://schema.org/InStock',
    out_of_stock: 'https://schema.org/OutOfStock',
    pre_order: 'https://schema.org/PreOrder',
    backorder: 'https://schema.org/BackOrder',
    unknown: 'https://schema.org/InStock',
};
/**
 * Make serialised JSON safe inside `<script type="application/ld+json">`.
 * Runs after JSON.stringify: `<`, `>`, `&`, U+2028 and U+2029 become
 * `\uXXXX` escapes, so merchant text can never close the script tag and
 * JSON.parse still returns the original characters.
 */
function wrapForScriptTag(body) {
    return body
        .replace(/</g, '\\u003c')
        .replace(/>/g, '\\u003e')
        .replace(/&/g, '\\u0026')
        .replace(/\u2028/g, '\\u2028')
        .replace(/\u2029/g, '\\u2029');
}
/** Short country-name alias table. Lowercase → ISO-3166 alpha-2. */
const COUNTRY_ALIAS = {
    us: 'US', 'united states': 'US', usa: 'US', 'u.s.': 'US', 'u.s.a.': 'US',
    ca: 'CA', canada: 'CA',
    uk: 'GB', 'u.k.': 'GB', 'united kingdom': 'GB', 'great britain': 'GB', britain: 'GB',
    gb: 'GB',
    au: 'AU', australia: 'AU',
    de: 'DE', germany: 'DE',
    fr: 'FR', france: 'FR',
    es: 'ES', spain: 'ES',
    it: 'IT', italy: 'IT',
    nl: 'NL', netherlands: 'NL',
    jp: 'JP', japan: 'JP',
};
function pickCountry(shipping_zones) {
    if (shipping_zones === undefined)
        return 'US';
    const token = shipping_zones.split(/[,/&]|\band\b/i)[0]?.trim().toLowerCase() ?? '';
    if (token.length === 0)
        return 'US';
    const direct = COUNTRY_ALIAS[token];
    if (direct !== undefined)
        return direct;
    if (/^[a-z]{2}$/.test(token))
        return token.toUpperCase();
    return 'US';
}
function pickReturnDays(return_window_days) {
    if (return_window_days === undefined)
        return 30;
    if (!/^\d{1,4}$/.test(return_window_days))
        return 30;
    const n = parseInt(return_window_days, 10);
    if (!Number.isFinite(n) || n < 1)
        return 30;
    return n;
}
function priceFromMinor(minor) {
    if (typeof minor !== 'number' || !Number.isFinite(minor) || minor < 0)
        return null;
    const whole = Math.floor(minor / 100);
    const cents = minor % 100;
    return `${whole}.${cents.toString().padStart(2, '0')}`;
}
/** Build one Product payload from a catalog product. Pure, deterministic. */
function buildProductPayload(product, domain, facts) {
    const d = normaliseDomain(domain);
    const country = pickCountry(facts.shipping_zones);
    const return_days = pickReturnDays(facts.return_window_days);
    const price = priceFromMinor(product.price_minor);
    const currency = (product.currency ?? facts.currency ?? 'USD').toUpperCase().slice(0, 3);
    const url = (product.url !== undefined && product.url.length > 0)
        ? product.url
        : `https://${d}/products/${product.catalog_product_id}`;
    const image = (product.image_url !== undefined && product.image_url.length > 0)
        ? product.image_url
        : `https://${d}/images/${product.catalog_product_id}.jpg`;
    const availability = CATALOG_AVAIL_TO_SCHEMA[product.availability ?? 'unknown']
        ?? CATALOG_AVAIL_TO_SCHEMA['unknown']
        ?? 'https://schema.org/InStock';
    // All merchant-controlled strings are escaped BEFORE JSON.stringify.
    // JSON.stringify handles `"` and `\n` for us; `escapeJsonLdValue`
    // neutralises `<` / `>` / `&` so a merchant string containing
    // `</script>` cannot escape the enclosing <script> tag.
    const payload = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.title,
        ...(product.sku !== undefined && product.sku.length > 0 ? { sku: product.sku } : {}),
        image: [image],
        ...(product.description !== undefined && product.description.length > 0
            ? { description: product.description }
            : {}),
        offers: {
            '@type': 'Offer',
            url: url,
            priceCurrency: currency,
            ...(price !== null ? { price } : { price: '<price>' }),
            availability,
            hasMerchantReturnPolicy: {
                '@type': 'MerchantReturnPolicy',
                applicableCountry: country,
                returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
                merchantReturnDays: return_days,
                returnMethod: 'https://schema.org/ReturnByMail',
                returnFees: 'https://schema.org/FreeReturn',
            },
            shippingDetails: {
                '@type': 'OfferShippingDetails',
                shippingRate: {
                    '@type': 'MonetaryAmount',
                    value: '<amount>',
                    currency,
                },
                shippingDestination: {
                    '@type': 'DefinedRegion',
                    addressCountry: country,
                },
                deliveryTime: {
                    '@type': 'ShippingDeliveryTime',
                    handlingTime: {
                        '@type': 'QuantitativeValue',
                        minValue: 0,
                        maxValue: 1,
                        unitCode: 'DAY',
                    },
                    transitTime: {
                        '@type': 'QuantitativeValue',
                        minValue: 2,
                        maxValue: 5,
                        unitCode: 'DAY',
                    },
                },
            },
        },
    };
    return payload;
}
/**
 * `catalogProductJsonLd` — ready-to-paste Product JSON-LD built from
 * one real catalog product. Prefills the merchant's real values
 * (name, SKU, image, description, URL, price, currency, availability)
 * and uses the agent-card profile's return window + shipping zone when
 * set. The output is wrapped in a `<script type="application/ld+json">`
 * tag, escaping `</script>` / U+2028 / U+2029 so merchant content
 * cannot break out.
 */
export function catalogProductJsonLd(product, domain, facts = {}) {
    const payload = buildProductPayload(product, domain, facts);
    return `<script type="application/ld+json">\n${wrapForScriptTag(JSON.stringify(payload, null, 2))}\n</script>`;
}
/* ================================================================== */
/* 7. Agent-card link tag                                              */
/* ================================================================== */
/**
 * `agentCardLinkTag` — the HTML tag merchants paste in <head> on
 * domains where Sill can't serve `/.well-known/agent-card.json` directly
 * (no plugin, no Worker). Points at the edge-hosted card for this
 * site. Falls back to the generic edge URL path when `site_key_hash`
 * is missing (installer hasn't produced one yet).
 */
export function agentCardLinkTag(site_key_hash) {
    const hash = (site_key_hash ?? '').trim();
    const href = hash.length > 0
        ? `https://edge.sill.so/v1/agent-card/${hash}.json`
        : 'https://edge.sill.so/v1/agent-card/<site_key_hash>.json';
    return `<link rel="agent-card" type="application/json" href="${href}">`;
}
/* ================================================================== */
/* 8. llms.txt starter                                                 */
/* ================================================================== */
/**
 * `llmsTxtStarter` — a short, honest default so a drawer can offer a
 * one-click paste when the merchant hasn't written anything yet. The
 * canonical editor lives in the Settings → llms.txt pane; this is the
 * fallback snippet.
 */
export function llmsTxtStarter(domain, name) {
    const d = normaliseDomain(domain);
    const n = (name ?? '').trim().length > 0 ? name.trim() : d;
    return [
        `# ${n}`,
        '',
        `${n} — a short, honest description of what the site sells or offers.`,
        '',
        '## Pages',
        `- Homepage: https://${d}/`,
        `- About: https://${d}/about`,
        `- Policies: returns, privacy, terms`,
        '',
    ].join('\n');
}
/* ================================================================== */
/* 9. Discrepancy field labels                                         */
/* ================================================================== */
/**
 * Merchant-readable label for a scan `discrepancy_flags[*].field` id.
 * Shared across the drawer and (eventually) the audit-bundle PDF.
 *
 * Keep in sync with `DISCREPANCY_FIELD_LABEL` in `readiness.ts` — the
 * ranker's headline helper reuses the same map. Both tables stay here
 * so a new flag is one additive line.
 */
export const DISCREPANCY_FIELD_LABEL = {
    shipping_zones: 'Shipping zones',
    return_window_days: 'Return window',
    returns_policy: 'Returns policy',
    currency: 'Currency',
    price_range: 'Price range',
    subscription_billing: 'Subscription cadence',
    skills: 'Skills',
    // 2026-10-02 — the pt_v4 scan emits free-text field ids like
    // `skills/commerce_applicability` when the model flags a mismatch
    // between the merchant's declared store skills and what the homepage
    // or policy pages say the site actually does. Pin the readable label
    // here so the drawer never shows the raw path to a merchant; the
    // fuzzy fallback in `discrepancyFieldLabel` handles any other
    // sub-paths without needing a new entry per flag.
    'skills/commerce_applicability': 'Store skills',
    name: 'Business name',
    description: 'Description',
    seller_tos_url: 'Terms of service',
    seller_privacy_policy_url: 'Privacy policy',
};
/**
 * Readable label for a scan `discrepancy_flags[*].field` id.
 *
 * The pt_v4 extraction (ADR-0184) emits free-text field ids. Known
 * ids (above) win. Unknown ids are humanized by (a) picking the LAST
 * path segment (`skills/commerce_applicability` → `commerce_applicability`),
 * (b) a keyword pass that recognises common commerce axes, and (c) a
 * final sentence-case fallback. Never return the raw field id to the
 * merchant — "skills/commerce_applicability" is debugger output, not a
 * label.
 */
export function discrepancyFieldLabel(field_id) {
    const direct = DISCREPANCY_FIELD_LABEL[field_id];
    if (direct !== undefined)
        return direct;
    const tail = field_id.includes('/')
        ? field_id.slice(field_id.lastIndexOf('/') + 1)
        : field_id;
    const lower = `${field_id} ${tail}`.toLowerCase();
    if (lower.includes('commerce'))
        return 'Store skills';
    if (lower.includes('skills'))
        return 'Skills';
    if (lower.includes('return'))
        return 'Return window';
    if (lower.includes('shipping'))
        return 'Shipping';
    if (lower.includes('refund'))
        return 'Refunds';
    if (lower.includes('currency'))
        return 'Currency';
    if (lower.includes('price'))
        return 'Price range';
    if (lower.includes('subscription'))
        return 'Subscription cadence';
    if (lower.includes('privacy'))
        return 'Privacy policy';
    if (lower.includes('terms'))
        return 'Terms of service';
    if (lower.includes('description'))
        return 'Description';
    // Final fallback — humanize + sentence-case.
    const cleaned = tail.replace(/[_-]+/g, ' ').trim();
    if (cleaned.length === 0)
        return field_id;
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}
/** Human label for an opaque discrepancy source string. */
const DISCREPANCY_SOURCE_LABEL = {
    agent_card: 'Agent card',
    card: 'Agent card',
    llms_txt: 'llms.txt',
    structured_data: 'Structured data',
    json_ld: 'Structured data',
    homepage: 'Homepage',
    home: 'Homepage',
    returns_page: 'Returns page',
    returns: 'Returns page',
    policy_page: 'Policy page',
    privacy_page: 'Privacy page',
    tos_page: 'Terms page',
    shipping_page: 'Shipping page',
    product_page: 'Product page',
    pricing_page: 'Pricing page',
    footer: 'Site footer',
    site: 'Website',
};
export function discrepancySourceLabels(sources) {
    if (sources.length === 0)
        return '—';
    return sources
        .map((s) => DISCREPANCY_SOURCE_LABEL[s] ?? s.replace(/_/g, ' '))
        .join(' + ');
}
/* ================================================================== */
/* 10. API catalog URL                                                 */
/* ================================================================== */
export function apiCatalogUrl(domain) {
    return `https://${normaliseDomain(domain)}/.well-known/api-catalog`;
}
/* ================================================================== */
/* 11. Agent-card edge URL (for a given site key hash)                 */
/* ================================================================== */
export function agentCardEdgeUrl(site_key_hash) {
    const hash = (site_key_hash ?? '').trim();
    return hash.length > 0
        ? `https://edge.sill.so/v1/agent-card/${hash}.json`
        : 'https://edge.sill.so/v1/agent-card/<site_key_hash>.json';
}
//# sourceMappingURL=readiness-fixes.js.map